import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  InputBase,
  Typography,
  Box,
  Popover,
  FormControl,
  ListItemButton,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getTenets } from "../../../api/tenets";
import { useSetLoading } from "../../hooks/appearance";

interface AutoSelectProps {
  setCompany: (company: { id: number | null; name: string | null }) => void;
}
const CompanyReportFilter: React.FC<AutoSelectProps> = ({
  setCompany,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedOption, setSelectedOption] = useState<{
    id: number;
    company_name: string;
  } | null>(null);
  const [prevAppliedId, setPrevAppliedId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [companyOptions, setCompanyNames] = useState<any[]>([]);


  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const getTenentList = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getTenets({ isActive: true });
      const items = response?.data?.data || [];
      const uniqueCompanies: { id: number; company_name: string }[] = [];

      for (const item of items) {
        if (item?.company_name) {
          uniqueCompanies.push({
            id: item.id,
            company_name: item.company_name,
          });
        }
      }
      setCompanyNames(uniqueCompanies);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getTenentList();
  }, [getTenentList]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
  };

  useEffect(() => {
    if (anchorEl && inputRef.current) {
      inputRef.current.focus();
    }
  }, [anchorEl]);

  const handleToggle = (option: { id: number; company_name: string }) => {
    const alreadySelected = selectedOption?.id === option.id;
    setSelectedOption(alreadySelected ? null : option);
  };

  const handleReset = () => {
    setSelectedOption(null);
    setCompany({ id: null, name: null });
    handleClose();
  };

  const handleApply = () => {
    const newId = selectedOption ? selectedOption.id : null;
    const newName = selectedOption ? selectedOption.company_name : null;
    if (newId !== prevAppliedId) {
      setCompany({ id: newId, name: newName });
      setPrevAppliedId(newId);
    }
    handleClose();
  };

  const filteredOptions = useMemo(
    () =>
      companyOptions.filter((opt) =>
        opt.company_name?.toLowerCase().includes(searchText?.toLowerCase())
      ),
    [companyOptions, searchText]
  );

  useSetLoading(isLoading);

  return (
    <Box>
      <div className="user-mgmt-form-auto-select-component">
        <FormControl fullWidth>
          <Box
            onClick={handleOpen}
            sx={{
              borderRadius: "4px",
              padding: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              height: "18px",
              background: "#fff",
            }}
          >
            <Typography sx={{ color: selectedOption ? "#000" : "#aaa" }}>
              {selectedOption ? selectedOption.company_name : "Select Company"}
            </Typography>
            <ExpandMoreIcon />
          </Box>

          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              style: {
                width: anchorEl?.clientWidth,
              },
            }}
          >
            <Box sx={{ padding: "8px", position: "relative" }}>
              <SearchIcon
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "16px",
                  transform: "translateY(-50%)",
                  color: "#8D8D8D",
                  pointerEvents: "none",
                  fontSize: 20,
                  zIndex: 1,
                }}
              />
              <InputBase
                inputRef={inputRef}
                placeholder="Search Company"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
                sx={{
                  padding: "8px 12px 8px 40px",
                  border: "1px solid #c2bcbc",
                  borderRadius: "6px",
                  backgroundColor: "#f5f5f5",
                  fontSize: "14px",
                  height: "37px",
                  color: "#000",
                }}
              />
            </Box>

            <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const selected = selectedOption?.id === option.id;
                  return (
                    <ListItemButton
                      key={option.id}
                      dense
                      disableGutters
                      onClick={() => handleToggle(option)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 16px",
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {option.company_name}
                      </Typography>
                      <Checkbox
                        checked={selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggle(option)}
                        sx={{
                          padding: 0,
                          "&.Mui-checked": {
                            color: "#1E3763",
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })
              ) : (
                <Typography sx={{ padding: "8px 16px", color: "#888" }}>
                  No companies found
                </Typography>
              )}
            </Box>

            {(selectedOption || searchText) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 1,
                  borderTop: "1px solid #e0e0e0",
                  background: "#fafafa",
                }}
              >
                <button
                  className="custom-reset-btn-user-mgmt-company-reset"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="custom-apply-btn-user-mgmt-company-apply"
                  onClick={handleApply}
                >
                  Apply
                </button>
              </Box>
            )}
          </Popover>
        </FormControl>
      </div>
    </Box>
  );
};

export default CompanyReportFilter;
